import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import useFocoPreso from '../../hooks/useFocoPreso';
import { ehSessaoTv } from '../../utils/andon';
import TextoMarkdown from './TextoMarkdown';
import { useAulaAberta } from './ajudaAulaStore';
import { DuvidaIAConversa, chaveDoErro, CHAVE_PROVA_ABERTA } from '../DuvidaIA';
import '../../styles/DuvidaIA.css';
import '../../styles/AjudaWidget.css';

// Bolha "Ajuda" (função de IA ajuda_plataforma, backend routes/ajudaPlataforma.js), no lugar do
// Tawk.to quando REACT_APP_SUPORTE_CHAT=ia (config/suporteChat.js). Ver docs/ia/IA-PLATAFORMA.md,
// "Ajuda com a plataforma".
//
// - Para todos os papéis logados, inclusive admin e exxer. Some sem login, na TV do Andon, com a
//   função desligada (GET /ajuda-plataforma/estado -> visivel false) e, para o aluno, com prova
//   aberta: a bolha inteira some (ajuda e dúvida da aula) e o servidor recusa as duas (em_prova).
// - Aluno dentro da aula (a tela de estudo registra a aula em ajudaAulaStore): a bolha oferece
//   "Dúvida sobre esta aula" (DuvidaIA, função duvidas_aluno) e "Ajuda com a plataforma". Fora da
//   aula, só a ajuda.
// - A conversa vive só nesta página (estado do componente): nada é gravado no servidor.
// - Encaminhamento: resposta `encaminhar_suporte` mostra "Abrir chamado no suporte", que leva a
//   /suporte com a descrição pré-preenchida (state da navegação, nunca na URL). Quem envia é a pessoa.

const HISTORICO_ENVIADO = 6;
const CONFERIR_PROVA_MS = 30000;
const ROTA_INICIAR_PROVA = /\/provas\/[^/]+\/iniciar$/;
const ROTA_RESPONDER_PROVA = /\/provas\/responder$/;

/** Sugestões rápidas do papel (textos em support:helpChat.suggestions.<papel>). */
export const sugestoesDoPapel = (t, papel) => {
  const s = t(`helpChat.suggestions.${papel}`, { returnObjects: true, defaultValue: {} });
  return s && typeof s === 'object' ? Object.values(s).filter((x) => typeof x === 'string') : [];
};

const AjudaPainel = ({ estado, mensagens, setMensagens, aoFechar, aoMudarEstado, aoProvaAberta, papel }) => {
  const { t, i18n } = useTranslation('support');
  const navigate = useNavigate();
  const location = useLocation();
  const idBase = useId();
  const id = (sufixo) => `${idBase}-${sufixo}`;
  const campoRef = useRef(null);
  const fimRef = useRef(null);
  const caixaRef = useFocoPreso(true, { aoFechar, focoInicial: campoRef });
  const [pergunta, setPergunta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState('');

  const ia = (estado && estado.ia) || {};
  const restantes = Number.isInteger(ia.restantesHoje) ? ia.restantesHoje : null;
  const perguntaMax = (estado && estado.limites && estado.limites.perguntaMax) || 1000;
  const indisponivel = !ia.disponivel;
  const motivoIndisponivel = indisponivel ? chaveDoErro(ia.motivo === 'bloqueado' ? 403 : 429, { motivo: ia.motivo }) : null;
  const podeAbrirChamado = !estado || !estado.suporte || estado.suporte.podeAbrirChamado !== false;
  const sugestoes = mensagens.length ? [] : sugestoesDoPapel(t, papel);

  useEffect(() => {
    if (fimRef.current && typeof fimRef.current.scrollIntoView === 'function') {
      fimRef.current.scrollIntoView({ block: 'end' });
    }
  }, [mensagens, enviando]);

  const enviar = async (textoPronto) => {
    const texto = (typeof textoPronto === 'string' ? textoPronto : pergunta).trim();
    if (texto.length < 2 || enviando || indisponivel) return;
    const historico = mensagens
      .filter((m) => m.papel === 'usuario' || m.papel === 'ia')
      .slice(-HISTORICO_ENVIADO)
      .map((m) => ({ papel: m.papel, texto: m.texto }));
    setMensagens((lista) => [...lista, { papel: 'usuario', texto }]);
    setPergunta('');
    setAviso('');
    setEnviando(true);
    try {
      const r = await api.post('/ajuda-plataforma', {
        pergunta: texto, historico, idioma: (i18n.language || 'pt').slice(0, 2), tela: location.pathname
      });
      const d = r.data || {};
      setMensagens((lista) => [...lista, {
        papel: 'ia', texto: d.resposta || '', situacao: d.situacao || 'ok', chamado: d.chamado || null
      }]);
      if (Number.isInteger(d.restantesHoje)) {
        aoMudarEstado((anterior) => (anterior ? {
          ...anterior,
          ia: {
            ...anterior.ia,
            restantesHoje: d.restantesHoje,
            disponivel: d.restantesHoje > 0,
            motivo: d.restantesHoje > 0 ? anterior.ia.motivo : 'limite_usuario'
          }
        } : anterior));
      }
    } catch (err) {
      const status = err.response && err.response.status;
      const dados = (err.response && err.response.data) || {};
      const chave = chaveDoErro(status, dados);
      // A pergunta que falhou volta para o campo; o erro aparece no painel.
      setMensagens((lista) => lista.slice(0, -1));
      setPergunta(texto);
      if (chave === 'inExam') {
        aoProvaAberta(dados.prova || { id: null });
        return;
      }
      setAviso(t(`helpChat.errors.${chave === 'lessonLocked' ? 'generic' : chave}`));
    } finally {
      setEnviando(false);
    }
  };

  const aoTeclarNoCampo = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      enviar();
    }
  };

  const abrirChamado = (texto) => {
    aoFechar();
    navigate('/suporte', { state: { descricaoChamado: texto || '' } });
  };

  return (
    <>
      <div className="ajuda-fundo" aria-hidden="true" onClick={aoFechar} />
      <section
        ref={caixaRef}
        className="duvida-ia-painel ajuda-painel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={id('titulo')}
        aria-describedby={id('subtitulo')}
        aria-busy={enviando}
        tabIndex={-1}
      >
        <header className="duvida-ia-topo">
          <div>
            <h2 id={id('titulo')}>{t('helpChat.title')}</h2>
            <p id={id('subtitulo')} className="duvida-ia-aula">{t('helpChat.subtitle')}</p>
          </div>
          <button type="button" className="duvida-ia-fechar" onClick={aoFechar} aria-label={t('helpChat.close')} title={t('helpChat.close')}>
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="duvida-ia-conversa" aria-live="polite" aria-relevant="additions">
          <div className="duvida-ia-msg ia">
            <span className="duvida-ia-autor">{t('helpChat.bot')}</span>
            <p className="duvida-ia-pergunta">{t('helpChat.greeting')}</p>
          </div>
          {sugestoes.length > 0 && !indisponivel && (
            <div className="ajuda-sugestoes" role="group" aria-label={t('helpChat.suggestionsLabel')}>
              {sugestoes.map((s) => (
                <button key={s} type="button" className="ajuda-sugestao" onClick={() => enviar(s)} disabled={enviando}>{s}</button>
              ))}
            </div>
          )}
          {mensagens.map((m, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className={`duvida-ia-msg ${m.papel === 'usuario' ? 'aluno' : 'ia'}${m.situacao === 'fora_do_escopo' ? ' fora' : ''}`}>
              <span className="duvida-ia-autor">{m.papel === 'usuario' ? t('helpChat.you') : t('helpChat.bot')}</span>
              {m.papel === 'usuario' ? <p className="duvida-ia-pergunta">{m.texto}</p> : <TextoMarkdown texto={m.texto} />}
              {m.situacao === 'fora_do_escopo' && <small className="duvida-ia-selo">{t('helpChat.outOfScope')}</small>}
              {m.situacao === 'encaminhar_suporte' && m.chamado && (
                m.chamado.podeAbrir
                  ? (
                    <button type="button" className="ajuda-chamado" onClick={() => abrirChamado(m.chamado.texto)}>
                      {t('helpChat.openTicket')}
                    </button>
                  )
                  : <small className="duvida-ia-selo">{t('helpChat.ticketTeam')}</small>
              )}
            </div>
          ))}
          {enviando && (
            <p className="duvida-ia-carregando" role="status">
              <span className="duvida-ia-spinner" aria-hidden="true" />{t('helpChat.thinking')}
            </p>
          )}
          <div ref={fimRef} />
        </div>

        {(aviso || indisponivel) && (
          <p className="duvida-ia-aviso" role="alert">
            {aviso || t(`helpChat.errors.${motivoIndisponivel === 'lessonLocked' ? 'generic' : motivoIndisponivel}`)}
          </p>
        )}

        <form className="duvida-ia-form" onSubmit={(e) => { e.preventDefault(); enviar(); }}>
          <label htmlFor={id('campo')} className="duvida-ia-rotulo">{t('helpChat.label')}</label>
          <textarea
            id={id('campo')}
            ref={campoRef}
            rows={2}
            maxLength={perguntaMax}
            value={pergunta}
            placeholder={t('helpChat.placeholder')}
            onChange={(e) => setPergunta(e.target.value)}
            onKeyDown={aoTeclarNoCampo}
            disabled={indisponivel}
            aria-describedby={id('restantes')}
          />
          <div className="duvida-ia-rodape">
            <small id={id('restantes')} className="duvida-ia-restantes">
              {restantes !== null ? t('helpChat.remaining', { count: restantes }) : ''}
            </small>
            <button type="submit" className="duvida-ia-enviar" disabled={enviando || indisponivel || pergunta.trim().length < 2}>
              {enviando ? t('helpChat.sending') : t('helpChat.send')}
            </button>
          </div>
          {podeAbrirChamado && (
            <small className="duvida-ia-nota">
              <button type="button" className="ajuda-link" onClick={() => abrirChamado('')}>{t('helpChat.talkToSupport')}</button>
            </small>
          )}
        </form>
      </section>
    </>
  );
};

// Menu da bolha quando há duas opções (aluno dentro da aula).
const AjudaMenu = ({ aoEscolher, aoFechar, aulaTitulo }) => {
  const { t } = useTranslation('support');
  const idTitulo = useId();
  const caixaRef = useFocoPreso(true, { aoFechar });
  return (
    <div ref={caixaRef} className="ajuda-menu" role="dialog" aria-modal="false" aria-labelledby={idTitulo} tabIndex={-1}>
      <p id={idTitulo} className="ajuda-menu-titulo">{t('helpChat.menuTitle')}</p>
      <button type="button" className="ajuda-menu-opcao" onClick={() => aoEscolher('duvida')} data-autofocus>
        <strong>{t('helpChat.optionLesson')}</strong>
        <small>{aulaTitulo ? t('helpChat.optionLessonHintTitle', { title: aulaTitulo }) : t('helpChat.optionLessonHint')}</small>
      </button>
      <button type="button" className="ajuda-menu-opcao" onClick={() => aoEscolher('ajuda')}>
        <strong>{t('helpChat.optionPlatform')}</strong>
        <small>{t('helpChat.optionPlatformHint')}</small>
      </button>
    </div>
  );
};

const AjudaWidget = () => {
  const { t } = useTranslation('support');
  const { usuario, loading } = useAuth();
  const location = useLocation();
  const aula = useAulaAberta();
  const botaoRef = useRef(null);

  const papel = usuario && usuario.tipo_usuario;
  const ehAluno = papel === 'aluno';
  const naTv = (location.pathname || '').startsWith('/andon/tv') || ehSessaoTv(usuario);
  const ativo = Boolean(usuario && !loading && !naTv);

  const [estadoAjuda, setEstadoAjuda] = useState(null);
  const [estadoDuvida, setEstadoDuvida] = useState(null);
  const [provaLocal, setProvaLocal] = useState(null);
  const [vista, setVista] = useState(null); // null | 'menu' | 'ajuda' | 'duvida'
  const [mensagens, setMensagens] = useState([]);

  const carregar = useCallback(async () => {
    if (!ativo) return;
    try {
      const r = await api.get('/ajuda-plataforma/estado');
      setEstadoAjuda(r.data || null);
      setProvaLocal(null);
    } catch (_err) {
      setEstadoAjuda(null);
    }
  }, [ativo]);

  const carregarDuvida = useCallback(async () => {
    if (!ativo || !ehAluno || !aula) { setEstadoDuvida(null); return; }
    try {
      const r = await api.get('/duvidas-aluno/estado');
      setEstadoDuvida(r.data || null);
    } catch (_err) {
      setEstadoDuvida(null);
    }
  }, [ativo, ehAluno, aula]);

  // Troca de usuário ou de papel: conversa nova.
  const sessao = usuario ? `${usuario.id}:${papel}` : '';
  useEffect(() => {
    setMensagens([]);
    setVista(null);
    setEstadoAjuda(null);
    if (sessao) carregar();
  }, [carregar, sessao]);

  useEffect(() => { carregarDuvida(); }, [carregarDuvida]);

  // Aluno: a prova pode abrir nesta aba (POST /provas/:id/iniciar), em outra aba (storage) ou
  // depois de um tempo; a bolha some e volta quando a prova é enviada.
  useEffect(() => {
    if (!ativo || !ehAluno) return undefined;
    const idInterceptor = api.interceptors && api.interceptors.response
      ? api.interceptors.response.use((resposta) => {
        const url = String((resposta && resposta.config && resposta.config.url) || '');
        const metodo = String((resposta && resposta.config && resposta.config.method) || '').toLowerCase();
        if (metodo === 'post' && ROTA_INICIAR_PROVA.test(url)) setProvaLocal({ id: null });
        if (metodo === 'post' && ROTA_RESPONDER_PROVA.test(url)) { carregar(); carregarDuvida(); }
        return resposta;
      })
      : null;
    const aoMudarStorage = (e) => { if (e.key === CHAVE_PROVA_ABERTA) { carregar(); carregarDuvida(); } };
    const aoVoltar = () => { if (document.visibilityState === 'visible') carregar(); };
    window.addEventListener('storage', aoMudarStorage);
    document.addEventListener('visibilitychange', aoVoltar);
    const timer = setInterval(() => { if (document.visibilityState !== 'hidden') carregar(); }, CONFERIR_PROVA_MS);
    return () => {
      if (idInterceptor !== null) api.interceptors.response.eject(idInterceptor);
      window.removeEventListener('storage', aoMudarStorage);
      document.removeEventListener('visibilitychange', aoVoltar);
      clearInterval(timer);
    };
  }, [ativo, ehAluno, carregar, carregarDuvida]);

  const emProva = Boolean(provaLocal || (estadoAjuda && estadoAjuda.emProva) || (estadoDuvida && estadoDuvida.emProva));
  const temAjuda = Boolean(estadoAjuda && estadoAjuda.visivel);
  const temDuvida = Boolean(ehAluno && aula && estadoDuvida && estadoDuvida.visivel);
  const visivel = ativo && !emProva && (temAjuda || temDuvida);

  const fechar = useCallback(() => {
    setVista(null);
    setTimeout(() => { if (botaoRef.current) botaoRef.current.focus(); }, 0);
    // A dúvida da aula pode ter fechado por prova aberta ou limite: confere de novo (sem custo).
    carregar();
    carregarDuvida();
  }, [carregar, carregarDuvida]);

  // Sumiu a opção aberta (prova, função desligada, saiu da aula): fecha.
  useEffect(() => {
    if (!visivel && vista) setVista(null);
    if (vista === 'duvida' && !temDuvida) setVista(null);
    if (vista === 'ajuda' && !temAjuda) setVista(null);
  }, [visivel, vista, temAjuda, temDuvida]);

  if (!visivel) return null;

  const abrir = () => {
    if (temAjuda && temDuvida) setVista((v) => (v === 'menu' ? null : 'menu'));
    else setVista(temDuvida ? 'duvida' : 'ajuda');
    carregar();
    carregarDuvida();
  };

  const aoProvaAberta = (prova) => {
    setProvaLocal(prova || { id: null });
    setVista(null);
  };

  return (
    <>
      {(vista === null || vista === 'menu') && (
        <button
          type="button"
          ref={botaoRef}
          className="ajuda-bolha"
          onClick={abrir}
          aria-haspopup="dialog"
          aria-expanded={vista === 'menu'}
          aria-label={t('helpChat.bubbleLabel')}
        >
          <span aria-hidden="true" className="ajuda-bolha-icone">?</span>
          <span>{t('helpChat.bubble')}</span>
        </button>
      )}
      {vista === 'menu' && createPortal(
        <AjudaMenu aoEscolher={setVista} aoFechar={fechar} aulaTitulo={aula && aula.aulaTitulo} />,
        document.body
      )}
      {vista === 'ajuda' && createPortal(
        <AjudaPainel
          estado={estadoAjuda}
          mensagens={mensagens}
          setMensagens={setMensagens}
          aoFechar={fechar}
          aoMudarEstado={setEstadoAjuda}
          aoProvaAberta={aoProvaAberta}
          papel={papel}
        />,
        document.body
      )}
      {temDuvida && (
        <DuvidaIAConversa
          turmaId={aula.turmaId}
          aulaId={aula.aulaId}
          aulaTitulo={aula.aulaTitulo}
          controlado
          aberto={vista === 'duvida'}
          aoFechar={fechar}
        />
      )}
    </>
  );
};

export default AjudaWidget;
